import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled, prefilledRange } from '../lib/reportPrefill';
import { defaultReportDate, defaultReportDateObj, maxReportDate, toReportDate } from '../lib/reportDates';
import ReportingCurrencyField from './ReportingCurrencyField';
import MaskedDateField from '../../../components/ui/MaskedDateField';

const HistoricalMarketValuePopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currency] = useState('USD');
  const [cadence, setCadence] = useState(() => prefilled(initialParams, 'cadence', 'auto'));
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const oneYearAgo = defaultReportDateObj();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const range = prefilledRange(initialParams, {
        start: toReportDate(oneYearAgo), end: defaultReportDate(),
      });
      setStartDate(range.start);
      setEndDate(range.end);
      setSelection({ selectedItem: null, params: {} });
      setErrors({});
    }
  }, [isOpen, initialParams]);

  // The single thin-params builder used by handleSubmit.
  const buildParams = (sel) => ({
    date_range: {
      start: startDate,
      end: endDate
    },
    currency: currency,
    cadence: cadence,
    ...sel.params,
  });

  const validateForm = (sel = selection) => {
    const newErrors = {};

    if (!sel.selectedItem) {
      newErrors.portfolio = 'Pick a group or account';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (startDate && endDate && startDate > endDate) {
      newErrors.dateRange = 'Start date must be before end date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validateForm(sel)) {
      return;
    }

    setIsLoading(true);

    try {
      await onConfirm(buildParams(sel));

      setSelection({ selectedItem: null, params: {} });
      setErrors({});
    } catch (error) {
      console.error('Error generating report:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelection({ selectedItem: null, params: {} });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      <div className={`relative w-full max-w-lg mx-4 rounded-lg shadow-xl ${
        darkMode ? 'bg-[#141414]' : 'bg-white'
      }`}>
        <div className={`flex items-center justify-between p-4 border-b ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <div>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Historical Market Value
            </h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {reportInfo?.description || 'Historical market value tracking'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`p-1 rounded-full hover:bg-gray-200 ${darkMode ? 'hover:bg-[#1a1a1a]' : ''}`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleSubmit}  disabled={isLoading}/>
            {errors.portfolio && (
              <p className="text-red-500 text-sm mt-1">{errors.portfolio}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Start Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={startDate}
              max={maxReportDate()}
              onChange={(e) => setStartDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.startDate ? 'border-red-500' : ''
              } ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-gray-500`}
             disabled={isLoading}/>
            {errors.startDate && (
              <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              End Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={endDate}
              max={maxReportDate()}
              onChange={(e) => setEndDate(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                errors.endDate ? 'border-red-500' : ''
              } ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-gray-500`}
             disabled={isLoading}/>
            {errors.endDate && (
              <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
            )}
            {errors.dateRange && (
              <p className="text-red-500 text-sm mt-1">{errors.dateRange}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Cadence
            </label>
            <select
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-gray-500`}
             disabled={isLoading}>
              <option value="auto">Auto (daily &le; 3 months, weekly &le; 13 months, else monthly)</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <ReportingCurrencyField darkMode={darkMode} />

          {errors.submit && (
            <div className="p-3 rounded-lg bg-red-100 text-red-700 text-sm">
              {errors.submit}
            </div>
          )}
        </div>

        <div className={`flex items-center justify-end gap-2 p-4 border-t ${
          darkMode ? 'border-white/10' : 'border-gray-200'
        }`}>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300' : ''}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className={darkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
          >
            {isLoading ? 'Generating...' : 'OK'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HistoricalMarketValuePopup;
